import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { adminCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  GripVertical,
  Eye,
  EyeOff,
  Image,
  ExternalLink,
  Handshake,
} from 'lucide-react';
import ImagePicker from '../../components/cms/ImagePicker';

const CmsPartners = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultPartner = {
    name: '',
    logo_url: '',
    website_url: '',
    order: 0,
    is_visible: true,
  };

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchPartners();
  }, [user, navigate]);

  const fetchPartners = async () => {
    try {
      const response = await adminCmsAPI.listPartners();
      setPartners(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingPartner.name) {
      toast.error('Le nom est requis');
      return;
    }

    setSaving(true);
    try {
      if (editingPartner.id) {
        await adminCmsAPI.updatePartner(editingPartner.id, editingPartner);
        toast.success('Partenaire mis à jour');
      } else {
        await adminCmsAPI.createPartner(editingPartner);
        toast.success('Partenaire créé');
      }
      setShowDialog(false);
      setEditingPartner(null);
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partenaire?')) return;
    
    try {
      await adminCmsAPI.deletePartner(id);
      toast.success('Partenaire supprimé');
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleVisibility = async (partner) => {
    try {
      await adminCmsAPI.updatePartner(partner.id, { is_visible: !partner.is_visible });
      toast.success(`Partenaire ${!partner.is_visible ? 'visible' : 'masqué'}`);
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-partners">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Partenaires</h1>
                <p className="text-white/60 text-sm">{partners.length} partenaire(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                setEditingPartner({ ...defaultPartner, order: partners.length });
                setShowDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau partenaire
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          {partners.map((partner) => (
            <Card key={partner.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="cursor-move text-neutral-400">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="w-16 h-16 bg-neutral-100 rounded flex items-center justify-center overflow-hidden">
                    {partner.logo_url ? (
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <Handshake className="w-8 h-8 text-neutral-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#1D1D1B]">{partner.name}</h3>
                      <Badge variant={partner.is_visible ? 'default' : 'secondary'}>
                        {partner.is_visible ? 'Visible' : 'Masqué'}
                      </Badge>
                    </div>
                    {partner.website_url && (
                      <a 
                        href={partner.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#F9B438] hover:underline flex items-center gap-1"
                      >
                        {partner.website_url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleVisibility(partner)}
                    >
                      {partner.is_visible ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-neutral-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingPartner(partner);
                        setShowDialog(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(partner.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {partners.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Handshake className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500 mb-4">Aucun partenaire pour le moment</p>
                <Button 
                  onClick={() => {
                    setEditingPartner({ ...defaultPartner });
                    setShowDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter le premier partenaire
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPartner?.id ? 'Modifier le partenaire' : 'Nouveau partenaire'}
            </DialogTitle>
          </DialogHeader>
          
          {editingPartner && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <ImagePicker value={editingPartner.logo_url || ''} onChange={url => setEditingPartner({ ...editingPartner, logo_url: url })} folder="general" label="Logo" />
              </div>

              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={editingPartner.name}
                  onChange={(e) => setEditingPartner({ ...editingPartner, name: e.target.value })}
                  placeholder="Nom du partenaire"
                />
              </div>

              <div className="space-y-2">
                <Label>Site web</Label>
                <Input
                  value={editingPartner.website_url || ''}
                  onChange={(e) => setEditingPartner({ ...editingPartner, website_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_visible"
                  checked={editingPartner.is_visible}
                  onChange={(e) => setEditingPartner({ ...editingPartner, is_visible: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_visible" className="cursor-pointer">
                  Visible sur le site
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingPartner?.id ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CmsPartners;
